import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createResourceLink,
  deleteResourceLink,
  getResourceMeta,
  getResources,
  updateResourceLink,
  type ResourceLinkInput,
} from '../api/resources';

export function useResources() {
  return useQuery({
    queryKey: ['resources'],
    queryFn: getResources,
  });
}

/** The admin editor's options: which features a link can be filed under. */
export function useResourceMeta(enabled: boolean) {
  return useQuery({
    queryKey: ['resources', 'meta'],
    queryFn: getResourceMeta,
    enabled,
  });
}

function useInvalidateResources() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['resources'] });
}

export function useCreateResourceLink() {
  const invalidate = useInvalidateResources();
  return useMutation({
    mutationFn: (input: ResourceLinkInput) => createResourceLink(input),
    onSuccess: invalidate,
  });
}

export function useUpdateResourceLink() {
  const invalidate = useInvalidateResources();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<ResourceLinkInput> & { id: string; verified?: boolean }) =>
      updateResourceLink(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteResourceLink() {
  const invalidate = useInvalidateResources();
  return useMutation({
    mutationFn: (id: string) => deleteResourceLink(id),
    onSuccess: invalidate,
  });
}
